import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  Button,
  Skeleton,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddShoppingCartOutlinedIcon from '@mui/icons-material/AddShoppingCartOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { useStores } from '../../hooks/useStores';
import { useProducts, useProductsTotal } from '../../hooks/useProducts';
import { getImageUrl } from '../../types/product';
import { useCartStore } from '../../stores/cartStore';
import { palette } from '../../theme/colors';

// Keyframe animations as inline styles
const fadeInUp = {
  animation: 'fadeInUp 0.8s ease-out forwards',
  opacity: 0,
};

const staggerDelay = (index: number) => ({
  animationDelay: `${index * 0.1}s`,
});

export const HomePage: React.FC = () => {
  const { data: stores, isLoading: storesLoading, error: storesError } = useStores();
  const { data: products, isLoading: productsLoading, error: productsError } = useProducts();
  const { data: productsTotal } = useProductsTotal();
  const addItem = useCartStore((state) => state.addItem);
  const heroRef = useRef<HTMLDivElement>(null);

  const featuredStores = stores?.slice(0, 4) || [];
  const featuredProducts = products?.slice(0, 8) || [];

  const handleAddToCart = (product: typeof featuredProducts[0]) => {
    addItem({
      productId: product.id,
      name: product.name,
      price: typeof product.price === 'string' ? parseFloat(product.price) : product.price,
      quantity: 1,
      locationId: product.locationId,
      locationName: product.location?.name || 'Unknown Store',
      image: product.images?.[0]?.thumbnail,
      organizationId: product.organizationId,
    });
  };

  // Add keyframes to document
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(40px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(2deg); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 0.4; }
        50% { opacity: 0.8; }
      }
      @keyframes slideIn {
        from { transform: translateX(-100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes scaleIn {
        from { transform: scale(0.8); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      @keyframes wave {
        0% { transform: translateX(0) translateZ(0) scaleY(1); }
        50% { transform: translateX(-25%) translateZ(0) scaleY(0.55); }
        100% { transform: translateX(-50%) translateZ(0) scaleY(1); }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', overflow: 'hidden' }}>
      {/* ============================================ */}
      {/* HERO SECTION - Immersive Editorial Style */}
      {/* ============================================ */}
      <Box
        ref={heroRef}
        sx={{
          position: 'relative',
          minHeight: { xs: 500, md: 600 },
          mb: 8,
          borderRadius: { xs: 0, md: 6 },
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${palette.sidebarBg} 0%, #1a2628 50%, #0d1516 100%)`,
        }}
      >
        {/* Animated wave background */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40%',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              width: '200%',
              height: '200%',
              bottom: '-50%',
              left: '-50%',
              background: `radial-gradient(ellipse at center, ${palette.primary}15 0%, transparent 70%)`,
              animation: 'wave 15s ease-in-out infinite',
            },
          }}
        />

        {/* Floating geometric shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            width: 300,
            height: 300,
            borderRadius: '50%',
            border: `1px solid ${palette.primary}30`,
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '15%',
            width: 150,
            height: 150,
            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
            background: `linear-gradient(135deg, ${palette.accent}20 0%, ${palette.primary}10 100%)`,
            animation: 'float 6s ease-in-out infinite',
            animationDelay: '-2s',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '30%',
            left: '8%',
            width: 80,
            height: 80,
            background: `${palette.tertiary}15`,
            transform: 'rotate(45deg)',
            animation: 'pulse 4s ease-in-out infinite',
          }}
        />

        {/* Grain overlay for texture */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Hero Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' },
            gap: 4,
            p: { xs: 3, md: 6 },
            minHeight: { xs: 500, md: 600 },
            alignItems: 'center',
          }}
        >
          {/* Left: Typography */}
          <Box>
            {/* Eyebrow */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                mb: 3,
                borderRadius: 50,
                bgcolor: `${palette.accent}20`,
                border: `1px solid ${palette.accent}40`,
                ...fadeInUp,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: palette.accent,
                  animation: 'pulse 2s ease-in-out infinite',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: palette.accent,
                  fontWeight: 700,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  fontFamily: '"Space Mono", monospace',
                }}
              >
                Multi-Store Marketplace
              </Typography>
            </Box>

            {/* Main headline */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
                fontWeight: 800,
                lineHeight: 0.95,
                color: '#fff',
                mb: 3,
                fontFamily: '"Clash Display", "Plus Jakarta Sans", sans-serif',
                letterSpacing: '-0.03em',
                ...fadeInUp,
                ...staggerDelay(1),
              }}
            >
              Reserve.
              <br />
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.sky} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Pick Up.
              </Box>
              <br />
              <Box component="span" sx={{ color: palette.accent }}>
                Enjoy.
              </Box>
            </Typography>

            {/* Subheadline */}
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontWeight: 400,
                lineHeight: 1.6,
                maxWidth: 480,
                mb: 4,
                fontFamily: '"DM Sans", sans-serif',
                ...fadeInUp,
                ...staggerDelay(2),
              }}
            >
              Discover products from{' '}
              <Box component="span" sx={{ color: palette.primary, fontWeight: 600 }}>
                {stores?.length || 0}+ local stores
              </Box>
              . Reserve online, pick up at your convenience. Smart shopping, reinvented.
            </Typography>

            {/* CTA Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                ...fadeInUp,
                ...staggerDelay(3),
              }}
            >
              <Button
                component={RouterLink}
                to="/products"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: palette.accent,
                  color: '#fff',
                  px: 4,
                  py: 1.75,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 50,
                  boxShadow: `0 20px 40px ${palette.accent}40`,
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': {
                    bgcolor: palette.accentDark,
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: `0 30px 60px ${palette.accent}50`,
                  },
                }}
              >
                Explore Products
              </Button>
              <Button
                component={RouterLink}
                to="/stores"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: '#fff',
                  px: 4,
                  py: 1.75,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 50,
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: palette.primary,
                    bgcolor: `${palette.primary}15`,
                  },
                }}
              >
                Browse Stores
              </Button>
            </Box>
          </Box>

          {/* Right: Stats Cards - Stacked asymmetrically */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'grid' },
              gridTemplateColumns: 'repeat(2, 1fr)',
              gridTemplateRows: 'repeat(2, auto)',
              gap: 2,
              transform: 'rotate(-2deg)',
              ...fadeInUp,
              ...staggerDelay(4),
            }}
          >
            {/* Stat 1 - Large */}
            <Box
              sx={{
                gridColumn: '1 / 2',
                gridRow: '1 / 3',
                p: 3,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 280,
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  bgcolor: palette.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <StorefrontOutlinedIcon sx={{ fontSize: 28, color: '#fff' }} />
              </Box>
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '3.5rem',
                    lineHeight: 1,
                    mb: 0.5,
                    fontFamily: '"Space Grotesk", sans-serif',
                  }}
                >
                  {stores?.length || 0}+
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    letterSpacing: '0.05em',
                  }}
                >
                  Active Stores
                </Typography>
              </Box>
            </Box>

            {/* Stat 2 */}
            <Box
              sx={{
                p: 3,
                borderRadius: 4,
                bgcolor: palette.sky,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 130,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                },
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 24, color: palette.textPrimary }} />
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 800,
                    fontFamily: '"Space Grotesk", sans-serif',
                  }}
                >
                  {productsTotal || products?.length || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: palette.textSecondary }}>
                  Products
                </Typography>
              </Box>
            </Box>

            {/* Stat 3 */}
            <Box
              sx={{
                p: 3,
                borderRadius: 4,
                background: `linear-gradient(135deg, ${palette.accent} 0%, ${palette.accentDark} 100%)`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 130,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                },
              }}
            >
              <LocalShippingOutlinedIcon sx={{ fontSize: 24, color: '#fff' }} />
              <Box>
                <Typography
                  variant="h4"
                  sx={{ color: '#fff', fontWeight: 800, fontFamily: '"Space Grotesk", sans-serif' }}
                >
                  Fast
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Pickup Service
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Scroll indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            color: 'rgba(255,255,255,0.4)',
          }}
        >
          <Typography variant="caption" sx={{ letterSpacing: '0.1em' }}>
            SCROLL
          </Typography>
          <Box
            sx={{
              width: 1,
              height: 40,
              bgcolor: 'rgba(255,255,255,0.2)',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '50%',
                bgcolor: palette.primary,
                animation: 'slideIn 1.5s ease-in-out infinite',
              },
            }}
          />
        </Box>
      </Box>

      {/* ============================================ */}
      {/* FEATURED STORES - Magazine Grid Layout */}
      {/* ============================================ */}
      <Box sx={{ mb: 10, px: { xs: 0, md: 2 } }}>
        {/* Section Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            mb: 5,
            pb: 3,
            borderBottom: `2px solid ${palette.border}`,
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: palette.primary,
                fontWeight: 700,
                letterSpacing: '0.2em',
                fontFamily: '"Space Mono", monospace',
              }}
            >
              Featured
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: palette.textPrimary,
                fontFamily: '"Clash Display", "Plus Jakarta Sans", sans-serif',
                letterSpacing: '-0.02em',
              }}
            >
              Local Stores
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="/stores"
            endIcon={<NorthEastIcon sx={{ fontSize: 18 }} />}
            sx={{
              color: palette.textSecondary,
              fontWeight: 600,
              '&:hover': {
                color: palette.primary,
                bgcolor: 'transparent',
              },
            }}
          >
            View All Stores
          </Button>
        </Box>

        {storesLoading && (
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rounded" height={280} sx={{ borderRadius: 4 }} />
              </Grid>
            ))}
          </Grid>
        )}

        {storesError && (
          <Alert severity="error" sx={{ borderRadius: 3 }}>
            Failed to load stores. Please try again later.
          </Alert>
        )}

        {/* Store Cards - Masonry-style */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {featuredStores.map((store, index) => (
            <Card
              key={store.id}
              component={RouterLink}
              to={`/stores/${store.id}`}
              sx={{
                textDecoration: 'none',
                position: 'relative',
                borderRadius: 4,
                overflow: 'hidden',
                bgcolor: index === 0 ? palette.sidebarBg : palette.surface,
                color: index === 0 ? '#fff' : palette.textPrimary,
                p: 3,
                minHeight: index === 0 ? 320 : 260,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gridRow: index === 0 ? 'span 1' : 'auto',
                border: index === 0 ? 'none' : `1px solid ${palette.border}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: index === 0
                    ? `0 30px 60px rgba(0,0,0,0.3)`
                    : `0 20px 50px ${palette.primary}20`,
                  '& .store-arrow': {
                    transform: 'translate(4px, -4px)',
                    opacity: 1,
                  },
                },
              }}
            >
              {/* Decorative pattern */}
              {index === 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '50%',
                    height: '100%',
                    background: `linear-gradient(135deg, transparent 0%, ${palette.primary}15 100%)`,
                  }}
                />
              )}

              {/* Store rank badge */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: index === 0 ? palette.accent : palette.sky,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  color: index === 0 ? '#fff' : palette.textPrimary,
                  fontFamily: '"Space Mono", monospace',
                }}
              >
                #{index + 1}
              </Box>

              {/* Store icon */}
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 3,
                  bgcolor: index === 0 ? 'rgba(255,255,255,0.1)' : palette.sky,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <StorefrontOutlinedIcon
                  sx={{
                    fontSize: 32,
                    color: index === 0 ? palette.primary : palette.textPrimary,
                  }}
                />
              </Box>

              {/* Store info */}
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                  }}
                >
                  {store.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.7,
                    mb: 2,
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {store.description || 'Discover amazing products from this store'}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {store.city && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PlaceOutlinedIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                      <Typography variant="caption" sx={{ opacity: 0.6 }}>
                        {store.city}
                      </Typography>
                    </Box>
                  )}
                  <Chip
                    label={store.type}
                    size="small"
                    sx={{
                      bgcolor: index === 0 ? 'rgba(255,255,255,0.15)' : palette.sky,
                      color: index === 0 ? '#fff' : palette.textSecondary,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      textTransform: 'capitalize',
                    }}
                  />
                </Box>
              </Box>

              {/* Hover arrow */}
              <Box
                className="store-arrow"
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  right: 20,
                  opacity: 0,
                  transition: 'all 0.3s ease',
                }}
              >
                <NorthEastIcon sx={{ fontSize: 20 }} />
              </Box>
            </Card>
          ))}
        </Box>
      </Box>

      {/* ============================================ */}
      {/* PRODUCTS SECTION - Bento Grid */}
      {/* ============================================ */}
      <Box sx={{ mb: 10 }}>
        {/* Section Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            mb: 5,
            pb: 3,
            borderBottom: `2px solid ${palette.border}`,
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: palette.accent,
                fontWeight: 700,
                letterSpacing: '0.2em',
                fontFamily: '"Space Mono", monospace',
              }}
            >
              Trending Now
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: palette.textPrimary,
                fontFamily: '"Clash Display", "Plus Jakarta Sans", sans-serif',
                letterSpacing: '-0.02em',
              }}
            >
              Featured Products
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="/products"
            endIcon={<NorthEastIcon sx={{ fontSize: 18 }} />}
            sx={{
              color: palette.textSecondary,
              fontWeight: 600,
              '&:hover': {
                color: palette.accent,
                bgcolor: 'transparent',
              },
            }}
          >
            View All Products
          </Button>
        </Box>

        {productsLoading && (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Skeleton variant="rounded" height={350} sx={{ borderRadius: 4 }} />
              </Grid>
            ))}
          </Grid>
        )}

        {productsError && (
          <Alert severity="error" sx={{ borderRadius: 3 }}>
            Failed to load products. Please try again later.
          </Alert>
        )}

        {/* Product Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {featuredProducts.map((product, index) => (
            <Card
              key={product.id}
              sx={{
                position: 'relative',
                borderRadius: 4,
                overflow: 'hidden',
                bgcolor: palette.surface,
                border: `1px solid ${palette.border}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 50px ${palette.primary}15`,
                  '& .product-image': {
                    transform: 'scale(1.08)',
                  },
                  '& .product-overlay': {
                    opacity: 1,
                  },
                },
              }}
            >
              {/* Product Image Container */}
              <Box
                component={RouterLink}
                to={`/products/${product.id}`}
                sx={{
                  position: 'relative',
                  height: 200,
                  bgcolor: palette.sky,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {product.images && product.images.length > 0 ? (
                  <Box
                    className="product-image"
                    component="img"
                    src={getImageUrl(product.images[0].thumbnail)}
                    alt={product.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      p: 3,
                      transition: 'transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    }}
                  />
                ) : (
                  <Inventory2OutlinedIcon sx={{ fontSize: 48, color: palette.textMuted }} />
                )}

                {/* Hover overlay with quick add */}
                <Box
                  className="product-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(0,0,0,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddToCart(product);
                    }}
                    sx={{
                      bgcolor: '#fff',
                      color: palette.textPrimary,
                      width: 56,
                      height: 56,
                      '&:hover': {
                        bgcolor: palette.accent,
                        color: '#fff',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <AddShoppingCartOutlinedIcon />
                  </IconButton>
                </Box>

                {/* Stock badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 50,
                    bgcolor: palette.success,
                    color: '#fff',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  In Stock
                </Box>
              </Box>

              {/* Product Info */}
              <Box sx={{ p: 2.5 }}>
                <Typography
                  component={RouterLink}
                  to={`/products/${product.id}`}
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: palette.textPrimary,
                    textDecoration: 'none',
                    display: 'block',
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: palette.primary,
                    },
                  }}
                >
                  {product.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: palette.textMuted,
                    display: 'block',
                    mb: 1.5,
                  }}
                >
                  {product.location?.name || 'Store'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: palette.textPrimary,
                      fontFamily: '"Space Grotesk", sans-serif',
                    }}
                  >
                    {typeof product.price === 'string'
                      ? parseFloat(product.price).toLocaleString('en-US')
                      : (product.price?.toLocaleString('en-US') || '0')}
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        color: palette.textMuted,
                        ml: 0.5,
                      }}
                    >
                      DZD
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>

        {/* View All CTA */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <Button
            component={RouterLink}
            to="/products"
            variant="outlined"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderColor: palette.border,
              color: palette.textPrimary,
              px: 6,
              py: 1.75,
              borderRadius: 50,
              borderWidth: 2,
              fontWeight: 600,
              '&:hover': {
                borderColor: palette.primary,
                borderWidth: 2,
                bgcolor: `${palette.primary}08`,
              },
            }}
          >
            Explore All Products
          </Button>
        </Box>
      </Box>

      {/* ============================================ */}
      {/* VALUE PROPOSITION BANNER */}
      {/* ============================================ */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 3,
          mb: 8,
          p: { xs: 3, md: 5 },
          borderRadius: 5,
          background: `linear-gradient(135deg, ${palette.sky}50 0%, ${palette.tertiary}30 100%)`,
          border: `1px solid ${palette.border}`,
        }}
      >
        {[
          {
            icon: <StorefrontOutlinedIcon sx={{ fontSize: 32 }} />,
            title: 'Multiple Stores',
            desc: 'Shop from various local stores in one place',
          },
          {
            icon: <LocalShippingOutlinedIcon sx={{ fontSize: 32 }} />,
            title: 'Quick Pickup',
            desc: 'Reserve online and pick up at your convenience',
          },
          {
            icon: <VerifiedOutlinedIcon sx={{ fontSize: 32 }} />,
            title: 'Verified Sellers',
            desc: '100% authentic products from trusted stores',
          },
        ].map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 3,
              p: 3,
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.9)',
                transform: 'translateY(-4px)',
              },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 3,
                bgcolor: palette.primary,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {item.icon}
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: palette.textPrimary,
                  mb: 0.5,
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: palette.textSecondary,
                  lineHeight: 1.6,
                }}
              >
                {item.desc}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
